import { useState } from 'react';
import { User, Camera, Save } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, TextArea, Select } from '@/components/ui/Input';
import { ProgressRing } from '@/components/ui/ProgressRing';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useProfile } from '@/hooks/useProfile';
import { calculateProfileCompleteness } from '@/types';
import toast from 'react-hot-toast';

export function ProfilePage() {
  const { profile, loading, update } = useProfile();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Record<string, unknown>>({});

  if (loading) return <LoadingSpinner />;
  if (!profile) return null;

  const completeness = calculateProfileCompleteness(profile);
  const merged = { ...profile, ...form };

  const handleSave = async () => {
    try {
      setSaving(true);
      await update(form as Partial<typeof profile>);
      setForm({});
      toast.success('Perfil actualizado');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key: string, value: unknown) => setForm(prev => ({ ...prev, [key]: value }));

  return (
    <div className="space-y-6 animate-fade-in">
      <h1 className="font-heading text-3xl tracking-wider">MI PERFIL</h1>

      {/* Profile header */}
      <Card className="flex items-center gap-4">
        <div className="relative">
          {profile.profile_photo_url ? (
            <img src={profile.profile_photo_url} alt="" className="w-20 h-20 rounded-full object-cover" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-brand-black flex items-center justify-center">
              <User size={32} className="text-brand-yellow" />
            </div>
          )}
          <button className="absolute -bottom-1 -right-1 w-7 h-7 bg-brand-yellow rounded-full flex items-center justify-center shadow-sm">
            <Camera size={14} className="text-brand-black" />
          </button>
        </div>
        <div className="flex-1">
          <h2 className="font-semibold text-lg">{profile.display_name || 'Sin nombre'}</h2>
          <Badge label={profile.driver_type === 'flotillero' ? 'Flotillero' : 'Independiente'} variant="black" />
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-gray-500">⭐ {profile.rating || 'N/A'}</span>
            <span className="text-xs text-gray-500">• {profile.total_routes_completed} rutas</span>
          </div>
        </div>
        <ProgressRing percentage={completeness.percentage} size={60} strokeWidth={5} />
      </Card>

      {completeness.missing.length > 0 && (
        <Card className="bg-brand-yellow/10 border border-brand-yellow">
          <p className="text-sm font-medium mb-1">Completa tu perfil para más oportunidades</p>
          <p className="text-xs text-gray-600">Falta: {completeness.missing.join(', ')}</p>
        </Card>
      )}

      {/* Edit form */}
      <Card padding="lg" className="space-y-4">
        <Input
          label="Nombre completo"
          value={String(merged.display_name || '')}
          onChange={(e) => updateField('display_name', e.target.value)}
        />
        <TextArea
          label="Sobre ti"
          value={String(merged.bio || '')}
          onChange={(e) => updateField('bio', e.target.value)}
          placeholder="Cuéntanos sobre tu experiencia..."
        />
        <Input
          label="Años de experiencia"
          type="number"
          value={String(merged.years_experience || '')}
          onChange={(e) => updateField('years_experience', parseInt(e.target.value) || null)}
        />
        <Select
          label="Tipo de conductor"
          value={String(merged.driver_type)}
          onChange={(e) => updateField('driver_type', e.target.value)}
          options={[
            { value: 'independiente', label: 'Independiente' },
            { value: 'flotillero', label: 'Flotillero' },
          ]}
        />
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={Boolean(merged.speaks_english)}
            onChange={(e) => updateField('speaks_english', e.target.checked)}
            className="w-4 h-4 accent-brand-yellow"
          />
          <span className="text-sm">Hablo inglés</span>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={Boolean(merged.has_helpers)}
            onChange={(e) => updateField('has_helpers', e.target.checked)}
            className="w-4 h-4 accent-brand-yellow"
          />
          <span className="text-sm">Cuento con ayudantes para maniobra</span>
        </div>

        {Object.keys(form).length > 0 && (
          <Button onClick={handleSave} loading={saving} fullWidth>
            <Save size={16} className="mr-1" />
            Guardar cambios
          </Button>
        )}
      </Card>
    </div>
  );
}
